import React, { useState, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { formatDate, useViewport, useAccessToken } from '@chilecompra/react-kit'
import { Tooltip, Typography } from '@chilecompra/react-kit/components'

import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { onOpenForcedEMTDialog } from '../ForcedEMTDialog/ForcedEMTDialog.actions'
import { onGetPrioritizedSupplierThunk } from '../QuoteSearchHeadingFilter/QuoteSearchHeadingFilters.actions'

import QuoteSupplierActivationDialog from './QuoteSupplierActivationDialog'

import { useProviderFileUrl, useMyPymeFileUrl } from '../../modules/hooks/dynamicUrlGenerator'
import { useHasReachedMaxDate } from '../../modules/hooks/maxDate'
import { inIframe } from '../../modules/utils/inIframe'
import { topPage } from '../../modules/utils/topPage'

import routes from '../../config/settings/routes'
import {
  QUOTE_STATUSES_COLOR_DICTIONARY,
  QUOTE_STATUSES_TRANSLATION_DICTIONARY,
  QUOTE_STATUSES_DICTIONARY_OPTIONS,
  QUOTE_STATUSES_DICTIONARY,
  EXPIRES_AT,
  QUOTE_STATUSES_PROVIDER
} from '../../config/settings/constants'

import {
  AlertIcon,
  DateBox,
  Grid,
  Link,
  TextOverflow,
  Label,
  LabelM,
  Tag,
  GridContainer,
  GridItem
} from './QuoteSearchResults.styles'

/*
 * The Row's component
 */
const Row = props => {
  const {
    activeResults,
    authInfo,
    closeDialogHabilityWarning,
    data: {
      call: isSecondCall,
      closeDate,
      creationDate,
      externalCode,
      myBids,
      name,
      organization,
      publishDate,
      purchaseOrderPending,
      status,
      unit
    },
    disabledInput,
    errosDisability,
    handleClickDialog,
    onClearAttachedFiles,
    open,
    openDialogHabilityWarning,
    supplierPrioritized
  } = props

  const {
    size: { isUpToMedium }
  } = useViewport()
  const navigate = useNavigate()
  const hasReachedMaxDate = useHasReachedMaxDate()
  const { isBuyer, isSeller } = useAuthorization()
  const accessToken = useAccessToken()
  const dispatch = useDispatch()

  const [statusActive, setStatusActive] = useState('')
  const emtCheckInFlight = useRef(false)

  const isInIframe = useMemo(() => {
    return inIframe()
  }, [])

  const handleAction = (externalCode, status) => async () => {
    if (emtCheckInFlight.current) return

    if (isBuyer && status === QUOTE_STATUSES_DICTIONARY_OPTIONS.SAVED) {
      onClearAttachedFiles()
      navigate({ pathname: routes.quoteStepOne, search: `?quote=${externalCode}` })
      return
    }

    if (isSeller && status === 'PUBLISHED') {
      if (activeResults?.status === QUOTE_STATUSES_PROVIDER.IN_PROGRESS) {
        openDialogHabilityWarning()
        setStatusActive(QUOTE_STATUSES_PROVIDER.IN_PROGRESS)
        return
      }
      if (activeResults?.status === QUOTE_STATUSES_PROVIDER.DISABLED) {
        openDialogHabilityWarning()
        setStatusActive(QUOTE_STATUSES_PROVIDER.DISABLED)
        return
      }
      if (activeResults?.status === QUOTE_STATUSES_PROVIDER.UNACREDITED) {
        openDialogHabilityWarning()
        setStatusActive(QUOTE_STATUSES_PROVIDER.UNACREDITED)
        return
      }

      emtCheckInFlight.current = true
      try {
        const refreshedPrioritized = await dispatch(onGetPrioritizedSupplierThunk({ orgTaxID: authInfo?.orgTaxID }))
        const isNoEMT = refreshedPrioritized ? refreshedPrioritized.isNoEMT : supplierPrioritized?.isNoEMT

        if (isNoEMT && !isSecondCall) {
          dispatch(onOpenForcedEMTDialog({ quoteId: externalCode }))
          topPage(isInIframe, true)
          return
        }
      } finally {
        emtCheckInFlight.current = false
      }
    }
    onClearAttachedFiles()
    navigate(routes.quoteSummary.replace(/:quoteId/, externalCode))
  }

  const textGoToBuyerSummary = status => {
    if (status === QUOTE_STATUSES_DICTIONARY_OPTIONS.SAVED && !authInfo.hasObserverRole) return 'Editar'
    if (status !== QUOTE_STATUSES_DICTIONARY_OPTIONS.SAVED || authInfo.hasObserverRole) return 'Ver detalle'
  }

  const textGoToSellerSummary = status => {
    if (status === QUOTE_STATUSES_DICTIONARY_OPTIONS.PUBLISHED) return myBids > 0 ? 'Ver detalle' : 'Participar'
    if (status !== QUOTE_STATUSES_DICTIONARY_OPTIONS.PUBLISHED) return 'Ver detalle'
  }

  const textGoToSummary = status => {
    if (isBuyer) return textGoToBuyerSummary(status)
    if (isSeller) return textGoToSellerSummary(status)
  }

  const showActionButton = useMemo(() => {
    if (!hasReachedMaxDate) {
      return true
    }

    const actionText = textGoToSummary(QUOTE_STATUSES_TRANSLATION_DICTIONARY[status])

    return actionText !== 'Editar'
  }, [status, hasReachedMaxDate])

  return (
    <Grid container alignItems="center">
      <QuoteSupplierActivationDialog
        statusActive={statusActive}
        externalCode={externalCode}
        onClearAttachedFiles={onClearAttachedFiles}
        providerFileUrl={useProviderFileUrl(authInfo.orgTaxID, isBuyer, EXPIRES_AT)}
        myPymeFileUrl={useMyPymeFileUrl(accessToken, authInfo.orgTaxID, isBuyer, EXPIRES_AT)}
        open={open}
        handleOnCloseDialog={closeDialogHabilityWarning}
        handleClickDialog={handleClickDialog}
        disabledInput={disabledInput}
        errosDisability={errosDisability}
      />

      <Grid container item md={6} alignItems="center" padding={!isUpToMedium ? '0 0 10px 0' : '0'}>
        {purchaseOrderPending && (
          <Grid item xs={12} md={1}>
            <Tooltip
              content="Tienes órdenes de compra que han cambiado de estado. Ingresa y revisa más detalles."
              placement="top"
            >
              <AlertIcon />
            </Tooltip>
          </Grid>
        )}
        <Grid item xs={12} md={11} data-testid={`CA-${externalCode}`}>
          <Grid alignItems="center" container item justifyContent="space-between">
            <Typography color="black3" variant="subtitle">
              {externalCode}
            </Typography>
            {!isUpToMedium && <Tag color={QUOTE_STATUSES_COLOR_DICTIONARY[status]} text={status || ''} />}
          </Grid>
          <Grid item>
            <TextOverflow title={name}>{name}</TextOverflow>
          </Grid>
          <Typography color="black3" variant="subtitle">
            {isBuyer && unit}
            {isSeller && `${organization} - ${unit}`}
          </Typography>
        </Grid>
      </Grid>
      <Grid alignItems="center" item container md sm>
        <Grid container direction="column" item md xs={6}>
          {QUOTE_STATUSES_DICTIONARY[QUOTE_STATUSES_TRANSLATION_DICTIONARY[status]] ===
            QUOTE_STATUSES_DICTIONARY.SAVED && (
            <DateBox textAlign="left">
              <Typography color="black3" variant="subtitle">
                Creada el
              </Typography>
              <Typography color="black1" variant="body1">
                {creationDate && formatDate(creationDate, 'dd/MM/yyyy')}
              </Typography>
              <Typography color="black3" variant="subtitle">
                {creationDate && formatDate(creationDate, 'HH:mm')}
              </Typography>
            </DateBox>
          )}
          {QUOTE_STATUSES_DICTIONARY[QUOTE_STATUSES_TRANSLATION_DICTIONARY[status]] !==
            QUOTE_STATUSES_DICTIONARY.SAVED && (
            <DateBox textAlign="left">
              <Typography color="black3" variant="subtitle">
                Publicada el
              </Typography>
              <Typography color="black1" variant="body1">
                {publishDate && formatDate(publishDate, 'dd/MM/yyyy')}
              </Typography>
              <Typography color="black3" variant="subtitle">
                {publishDate && formatDate(publishDate, 'HH:mm')}
              </Typography>
            </DateBox>
          )}
        </Grid>
        <Grid container direction="column" item md xs={6}>
          <DateBox textAlign="right">
            <Typography color="black3" variant="subtitle">
              Finaliza el
            </Typography>
            <Typography color="black1" variant="body1">
              {formatDate(closeDate, 'dd/MM/yyyy')}
            </Typography>
            <Typography color="black3" variant="subtitle">
              {formatDate(closeDate, 'HH:mm')}
            </Typography>
          </DateBox>
        </Grid>
        <Grid container item md>
          <Grid item xs={12}>
            {isUpToMedium && <Tag color={QUOTE_STATUSES_COLOR_DICTIONARY[status]} text={status || ''} />}
          </Grid>
          <br />
          {isSecondCall && isUpToMedium && (
            <Grid container item xs={12} justifyContent="center" alignItems="center" style={{ textAlign: 'center' }}>
              <Label> SEGUNDO LLAMADO </Label>
            </Grid>
          )}
          {isSecondCall && !isUpToMedium && (
            <Grid container item xs={12} justifyContent="center" alignItems="center" style={{ textAlign: 'center' }}>
              <LabelM> SEGUNDO LLAMADO </LabelM>
            </Grid>
          )}
        </Grid>
        {showActionButton && (
          <GridContainer
            xs={12}
            sm={12}
            md={12}
            lg={3}
            container
            justifyContent="center"
            alignItems="center"
            padding={!isUpToMedium ? '12px 0 50px 0' : '0px'}
          >
            <GridItem style={{ textAlign: 'center' }}>
              <Link onClick={handleAction(externalCode, QUOTE_STATUSES_TRANSLATION_DICTIONARY[status])}>
                {textGoToSummary(QUOTE_STATUSES_TRANSLATION_DICTIONARY[status])}
              </Link>
            </GridItem>
          </GridContainer>
        )}
      </Grid>
    </Grid>
  )
}

Row.defaultProps = {
  myBids: 0,
  onClearAttachedFiles: () => undefined,
  handleOpenDialogHabilityWarning: () => undefined,
  handleOnClose: () => undefined,
  handleClickDialog: () => undefined,
  disabledInput: () => undefined,
  errosDisability: () => undefined,
  handleClickPrioritized: () => undefined
}

Row.propTypes = {
  activeResults: PropTypes.shape({
    status: PropTypes.string.isRequired
  }),
  authInfo: PropTypes.shape({
    hasObserverRole: PropTypes.bool,
    orgTaxID: PropTypes.string
  }),
  closeDialogHabilityWarning: PropTypes.func,
  data: PropTypes.shape({
    closeDate: PropTypes.string.isRequired,
    creationDate: PropTypes.string,
    externalCode: PropTypes.string.isRequired,
    myBids: PropTypes.number,
    name: PropTypes.string.isRequired,
    organization: PropTypes.string.isRequired,
    publishDate: PropTypes.string,
    purchaseOrderPending: PropTypes.bool.isRequired,
    status: PropTypes.string.isRequired,
    unit: PropTypes.string.isRequired,
    call: PropTypes.bool.isRequired
  }).isRequired,
  disabledInput: PropTypes.func,
  errosDisability: PropTypes.func,
  handleClickDialog: PropTypes.func,
  onClearAttachedFiles: PropTypes.func,
  open: PropTypes.bool,
  openDialogHabilityWarning: PropTypes.func,
  supplierPrioritized: PropTypes.shape({
    isNoEMT: PropTypes.bool
  })
}

export default Row
